/**
 * API Service for fetching blog data from backend
 */

import { BlogCategory, BlogPost, BlogListResponse } from '../types/blog';

// API base URL - can be configured via environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Fetch blog categories
 * @param lang Language code (en, zh, fr, es)
 * @param fallbackLang Fallback language if requested language not available
 * @returns Array of blog categories
 */
export const fetchBlogCategories = async (
  lang: string = 'en',
  fallbackLang: string = 'en'
): Promise<BlogCategory[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/blogs/categories?lang=${lang}`);

    if (!response.ok) {
      // If primary language fails and it's not the fallback language, try fallback
      if (lang !== fallbackLang) {
        console.warn(`Failed to fetch categories in ${lang}, trying ${fallbackLang}`);
        const fallbackResponse = await fetch(
          `${API_BASE_URL}/web/blogs/categories?lang=${fallbackLang}`
        );
        if (fallbackResponse.ok) {
          return await fallbackResponse.json();
        }
      }
      throw new Error(`Failed to fetch categories: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching blog categories:', error);
    throw error;
  }
};

/**
 * Fetch blog posts list with pagination
 * @param options Fetch options including language, pagination, and filters
 * @returns Blog list response with pagination info
 */
export const fetchBlogPosts = async (options: {
  lang?: string;
  fallbackLang?: string;
  category?: string;
  page?: number;
  pageSize?: number;
  featuredOnly?: boolean;
}): Promise<BlogListResponse> => {
  const {
    lang = 'en',
    fallbackLang = 'en',
    category,
    page = 1,
    pageSize = 10,
    featuredOnly = false,
  } = options;

  try {
    const params = new URLSearchParams({
      lang,
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (category) {
      params.append('category', category);
    }

    if (featuredOnly) {
      params.append('featured_only', 'true');
    }

    const response = await fetch(`${API_BASE_URL}/web/blogs?${params.toString()}`);

    if (!response.ok) {
      // If primary language fails and it's not the fallback language, try fallback
      if (lang !== fallbackLang) {
        console.warn(`Failed to fetch blog posts in ${lang}, trying ${fallbackLang}`);
        params.set('lang', fallbackLang);
        const fallbackResponse = await fetch(`${API_BASE_URL}/web/blogs?${params.toString()}`);
        if (fallbackResponse.ok) {
          return await fallbackResponse.json();
        }
      }
      throw new Error(`Failed to fetch blog posts: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

/**
 * Fetch a single blog post by slug
 * @param slug Blog post slug
 * @param lang Language code
 * @param fallbackLang Fallback language if requested language not available
 * @returns Blog post details
 */
export const fetchBlogPost = async (
  slug: string,
  lang: string = 'en',
  fallbackLang: string = 'en'
): Promise<BlogPost> => {
  try {
    const response = await fetch(`${API_BASE_URL}/web/blogs/${slug}?lang=${lang}`);

    if (!response.ok) {
      // If primary language fails and it's not the fallback language, try fallback
      if (lang !== fallbackLang) {
        console.warn(`Failed to fetch blog post in ${lang}, trying ${fallbackLang}`);
        const fallbackResponse = await fetch(
          `${API_BASE_URL}/web/blogs/${slug}?lang=${fallbackLang}`
        );
        if (fallbackResponse.ok) {
          return await fallbackResponse.json();
        }
      }
      throw new Error(`Failed to fetch blog post: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

/**
 * Fetch blog posts by category
 * @param categorySlug Category slug
 * @param options Additional options
 * @returns Blog list response
 */
export const fetchBlogPostsByCategory = async (
  categorySlug: string,
  options: {
    lang?: string;
    fallbackLang?: string;
    page?: number;
    pageSize?: number;
  } = {}
): Promise<BlogListResponse> => {
  const { lang = 'en', fallbackLang = 'en', page = 1, pageSize = 10 } = options;

  try {
    const params = new URLSearchParams({
      lang,
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    const response = await fetch(
      `${API_BASE_URL}/web/blogs/category/${categorySlug}?${params.toString()}`
    );

    if (!response.ok) {
      // If primary language fails and it's not the fallback language, try fallback
      if (lang !== fallbackLang) {
        console.warn(`Failed to fetch category posts in ${lang}, trying ${fallbackLang}`);
        params.set('lang', fallbackLang);
        const fallbackResponse = await fetch(
          `${API_BASE_URL}/web/blogs/category/${categorySlug}?${params.toString()}`
        );
        if (fallbackResponse.ok) {
          return await fallbackResponse.json();
        }
      }
      throw new Error(`Failed to fetch category posts: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching category blog posts:', error);
    throw error;
  }
};
